import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui-kit";
import { Siren, X, CheckCircle, Phone } from "@phosphor-icons/react";

/**
 * SOS floating button + confirmation sheet.
 * When triggered: sends incident with optional ride_id + current location.
 */
export default function SOSButton({ rideId = null, className = "" }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [note, setNote] = useState("");

  const trigger = async () => {
    setSubmitting(true);
    let lat = null, lng = null;
    if (navigator.geolocation) {
      try {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => { lat = pos.coords.latitude; lng = pos.coords.longitude; resolve(); },
            () => resolve(),
            { timeout: 5000 }
          );
        });
      } catch {}
    }
    try {
      await api.post("/sos/trigger", { ride_id: rideId, lat, lng, note: note || null });
      setDone(true);
      toast.success("Alerte envoyée — un opérateur va vous contacter");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(true); setDone(false); setNote(""); }}
        className={`flex items-center gap-2 rounded-full bg-destructive px-4 py-3 text-xs font-bold uppercase tracking-widest text-destructive-foreground shadow-lg press-down ${className}`}
        style={{ boxShadow: "0 0 24px 0 rgba(220,38,38,0.5)" }}
        data-testid="sos-trigger"
      >
        <Siren size={18} weight="fill" /> SOS
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[950] bg-black/70 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ y: 300, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 300, opacity: 0 }}
              className="fixed inset-x-0 bottom-0 z-[960] mx-auto max-w-[540px] px-2 pb-2"
              data-testid="sos-sheet"
            >
              <div className="max-h-[82dvh] overflow-y-auto rounded-3xl border-2 border-destructive/40 glass-strong p-4 min-[390px]:p-5" style={{ boxShadow: "0 0 40px 0 rgba(220,38,38,0.35)" }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Siren size={32} weight="fill" className="text-destructive" />
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-widest text-destructive">Urgence</div>
                      <h2 className="font-heading text-2xl font-bold">Déclencher SOS</h2>
                    </div>
                  </div>
                  <button onClick={() => setOpen(false)} className="rounded-full bg-white/10 p-2 press-down" data-testid="sos-close">
                    <X size={18} weight="bold" />
                  </button>
                </div>

                {done ? (
                  <div className="py-6 text-center">
                    <CheckCircle size={48} weight="duotone" className="mx-auto text-secondary" />
                    <div className="mt-2 font-heading text-xl font-bold">Alerte envoyée</div>
                    <div className="mt-1 text-sm text-muted-foreground">Un opérateur va vous contacter.</div>
                    <a href="tel:112" className="mt-4 inline-flex items-center gap-2 rounded-full bg-destructive px-5 py-3 text-sm font-bold text-destructive-foreground press-down" data-testid="sos-call-112">
                      <Phone weight="fill" /> Appeler le 112
                    </a>
                  </div>
                ) : (
                  <>
                    <div className="mt-3 text-sm text-muted-foreground">
                      Nous enverrons votre position GPS et les détails de la course à l'équipe NEXORA.
                    </div>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Décrivez la situation (optionnel)…"
                      rows={3}
                      className="mt-3 w-full resize-none rounded-xl border border-white/10 bg-white/5 p-3 text-sm placeholder:text-muted-foreground focus:border-destructive focus:outline-none"
                      data-testid="sos-note"
                    />
                    <Button
                      variant="danger"
                      className="mt-3 w-full py-4 text-base"
                      onClick={trigger}
                      disabled={submitting}
                      data-testid="sos-confirm"
                    >
                      {submitting ? "Envoi..." : "Déclencher l'alerte"}
                    </Button>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
